import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Database, Cloud, Layers, Brain, BarChart2, Package, ArrowRight } from 'lucide-react';
import FadeInUp from '../components/FadeInUp';
import SEO from '../components/SEO';

const services = [
  {
    course: 'First Course',
    icon: <Database className="w-4 h-4" />,
    title: 'Enterprise Data Architecture',
    description:
      'We design the structural foundation of your data ecosystem — a platform that scales, governs, and accelerates every downstream initiative.',
    ingredients: ['Azure Synapse', 'Snowflake', 'Databricks', 'BigQuery', 'Data Vault 2.0'],
    dish: [
      'Future-proof data platform blueprint',
      'Technology selection and roadmap',
      'Governance framework design',
      'Reference architecture documentation',
    ],
  },
  {
    course: 'Second Course',
    icon: <Cloud className="w-4 h-4" />,
    title: 'Cloud Platform Engineering',
    description:
      'From greenfield deployments to complex migrations, we engineer cloud environments that balance performance, security, and cost.',
    ingredients: ['Azure', 'AWS', 'GCP', 'Terraform', 'Infrastructure as Code'],
    dish: [
      'Cloud migration strategy and execution',
      'Cost-optimised infrastructure',
      'Multi-cloud governance',
      'DevOps and CI/CD pipelines',
    ],
  },
  {
    course: 'Third Course',
    icon: <Layers className="w-4 h-4" />,
    title: 'Data Engineering',
    description:
      'We build the pipelines and transformations that turn disparate data sources into a unified, trusted, analytics-ready foundation.',
    ingredients: ['Apache Spark', 'Python', 'SQL', 'dbt', 'AirFlow', 'Kafka'],
    dish: [
      'Production-grade data pipelines',
      'Real-time and batch processing',
      'Data quality frameworks',
      'Lakehouse architecture',
    ],
  },
  {
    course: 'Fourth Course',
    icon: <Brain className="w-4 h-4" />,
    title: 'AI & ML Enablement',
    description:
      'We prepare your data platform and teams for the AI era — from infrastructure through to production model deployment.',
    ingredients: ['MLflow', 'Azure ML', 'SageMaker', 'Vertex AI', 'LLMs'],
    dish: [
      'ML infrastructure and MLOps design',
      'Model deployment pipelines',
      'AI use case identification',
      'Responsible AI governance',
    ],
  },
  {
    course: 'Fifth Course',
    icon: <BarChart2 className="w-4 h-4" />,
    title: 'Platform Strategy',
    description:
      'We help leaders align technology investments with business strategy, building the operating model that sustains long-term data excellence.',
    ingredients: ['Enterprise roadmaps', 'Operating models', 'Data mesh', 'Team topology'],
    dish: [
      'Data platform maturity assessment',
      'Strategic investment roadmap',
      'Data team operating model',
      'Vendor evaluation and selection',
    ],
  },
  {
    course: "Chef's Special",
    icon: <Package className="w-4 h-4" />,
    title: 'Productised Engagements',
    description:
      'Pre-packaged consulting services with transparent pricing, fixed timelines, and guaranteed deliverables — the precision of bespoke, the efficiency of product.',
    ingredients: ['Fixed scope', 'Defined timelines', 'Clear deliverables', 'Transparent pricing'],
    dish: [
      '2-week data platform assessment',
      '4-week cloud migration sprint',
      '1-week architecture review',
      'Data strategy in a day',
    ],
  },
];

const Services = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero */}
      <SEO title="Services" description="Enterprise data consulting services — architecture, cloud engineering, data pipelines, AI/ML enablement, platform strategy, and productised engagements." path="/services" />
      <section className="px-8 md:px-16 lg:px-24 pt-40 pb-20">
        <p className="text-xs tracking-[0.3em] uppercase text-silver mb-6">The Menu</p>
        <p className="text-lg text-soft-white max-w-lg leading-relaxed">
          Each engagement is crafted with the precision of a tasting menu — defined ingredients,
          masterful preparation, and exceptional outcomes.
        </p>
      </section>

      {/* Services List */}
      <section className="px-8 md:px-16 lg:px-24 pb-20">
        {services.map((service, index) => (
          <div key={index}>
            {/* Animated line reveal */}
            <motion.div
              className="h-px bg-gray-800"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              style={{ originX: 0 }}
            />
            <FadeInUp>
              <div className="py-16 md:py-20">
                <div className="grid md:grid-cols-3 gap-12 md:gap-16">
                  {/* Left: Course + Title + Description */}
                  <div>
                    <div className="flex items-center space-x-2 text-silver mb-4">
                      {service.icon}
                      <span className="text-xs tracking-[0.2em] uppercase">{service.course}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-light mb-5">{service.title}</h2>
                    <p className="text-silver leading-relaxed text-sm">{service.description}</p>
                  </div>

                  {/* Middle: The Dish */}
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-silver mb-5">The Dish</p>
                    <ul className="space-y-4">
                      {service.dish.map((item) => (
                        <li key={item} className="flex items-start space-x-3 text-base text-soft-white">
                          <ArrowRight className="w-4 h-4 text-silver flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Right: Ingredients */}
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-silver mb-5">Ingredients</p>
                    <div className="flex flex-wrap gap-2">
                      {service.ingredients.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-soft-white border border-outline rounded px-3 py-1"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </FadeInUp>
          </div>
        ))}
        <motion.div
          className="h-px bg-gray-800"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ originX: 0 }}
        />
      </section>

      {/* CTA */}
      <FadeInUp>
        <section className="mx-8 md:mx-16 lg:mx-24 mb-20 bg-outline border border-outline rounded-lg py-20 px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-4">Not sure which service fits?</h2>
          <p className="text-silver mb-10 max-w-md mx-auto leading-relaxed">
            Book a complimentary consultation and we'll help you find the right starting point.
          </p>
          <Link to="/contact">
            <Button
              variant="outline"
              className="border-cloud text-white hover:bg-cloud hover:text-black px-10 py-6 text-xs tracking-widest uppercase transition-all duration-300"
            >
              Book a Consultation
            </Button>
          </Link>
        </section>
      </FadeInUp>
    </div>
  );
};

export default Services;
